import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DealCard, { type Deal } from './DealCard';
import { getTranslatedName, getCurrentLang } from '@/lib/translation-utils';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

export type Stage = { id: string; name: string; name_hu?: string | null; name_sk?: string | null; name_en?: string | null; wipLimit?: number | null; measure?: boolean | null };

const KanbanColumn = React.forwardRef<HTMLDivElement, {
  stage: Stage;
  deals: Deal[];
  totalDeals?: number; // Total number of deals in this stage (not just loaded)
  /** Count of deals in this stage with overdue closing date (same rule as DealCard red date). */
  overdueClosingCount?: number;
  onDropDeal: (dealId: string, stageId: string) => void;
  onOpenDeal?: (id: string) => void;
  onCreateDeal?: (stageId: string) => void;
  collapsed?: boolean;
  onToggleCollapse?: (stageId: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  selectionMode?: boolean;
  selectedDeals?: Set<string>;
  onSelectDeal?: (dealId: string, checked: boolean) => void;
  onSelectStage?: (stageId: string) => void;
}>(function KanbanColumn({
  stage,
  deals,
  totalDeals,
  overdueClosingCount = 0,
  onDropDeal,
  onOpenDeal,
  onCreateDeal,
  collapsed,
  onToggleCollapse,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  selectionMode = false,
  selectedDeals = new Set(),
  onSelectDeal,
  onSelectStage,
}, ref) {
  const { t, i18n } = useTranslation();
  const lang = getCurrentLang(i18n.language);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  
  // Intersection Observer for load more trigger
  useEffect(() => {
    if (!loadMoreRef.current || !onLoadMore || !hasMore || collapsed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [onLoadMore, hasMore, isLoading, collapsed]);

  const overLimit = stage.wipLimit != null && deals.length > (stage.wipLimit || 0);
  const baseCls = `${overLimit ? 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-800' : 'bg-muted/30'} rounded-lg border border-border`;

  if (collapsed) {
    return (
      <div 
        ref={ref}
        className={`relative ${baseCls} w-14 h-full cursor-pointer overflow-hidden flex-none`} 
        onClick={() => onToggleCollapse?.(stage.id)}
      >
        {/* Rotated stage name along the column, padded from top */}
        <div className="absolute inset-x-0 top-3 flex justify-center">
          <h3 className="font-semibold text-sm text-foreground/88 writing-vertical text-center" title={getTranslatedName(stage, lang)}>{getTranslatedName(stage, lang)}</h3>
        </div>
        {/* Centered, rotated count with same styling as open columns; keep on one line */}
        <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-90 text-xs font-light whitespace-nowrap ${overLimit ? 'text-red-600/70' : 'text-foreground/50'}`}>{totalDeals ?? deals.length} / {overdueClosingCount} {t("crm.deal")}{stage.wipLimit ? ` / ${stage.wipLimit}` : ''}</div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        "relative group flex flex-col h-full overflow-hidden",
        isMobile ? "pt-3 pb-0 px-6 flex-none w-[84%] min-w-[84%] max-w-[84%] snap-center" : "pt-3 pb-0 px-1.5 flex-1",
        baseCls
      )}
      style={isMobile ? undefined : { minWidth: '210px' }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        const id = e.dataTransfer.getData('text/dealId');
        if (id) onDropDeal(id, stage.id);
      }}
    >
      <div className={cn(
        "text-center relative group/header",
        isMobile ? "mb-2.5" : "mb-2"
      )}>
        <h3 className="text-base font-semibold text-foreground/88 truncate" title={getTranslatedName(stage, lang)}>{getTranslatedName(stage, lang)}</h3>
        <div className={cn(
          "font-light",
          isMobile ? "text-sm mt-1" : "text-xs mt-0.5",
          overLimit ? "text-red-600/70" : "text-foreground/50"
        )}>{totalDeals ?? deals.length} / {overdueClosingCount} {t("crm.deal")}{stage.wipLimit ? ` / ${stage.wipLimit}` : ''}</div>
      </div>

      <div className={cn(
        "flex-1 min-h-0 overflow-y-auto pb-0",
        isMobile ? "space-y-2.5" : "space-y-2",
        !isMobile && "group-hover:pb-20"
      )}>
        {deals.map((d) => (
          <DealCard 
            deal={d as any} 
            key={d.id} 
            onOpen={onOpenDeal}
            selected={selectedDeals.has(d.id)}
            onSelect={onSelectDeal}
            selectionMode={selectionMode}
          />
        ))}
        {/* Load more trigger */}
        {hasMore && (
          <div ref={loadMoreRef} className={cn("flex items-center justify-center", isMobile ? "h-6 py-2" : "h-4")}>
            {isLoading && (
              <div className="text-xs text-muted-foreground">{t("common.loading")}</div>
            )}
          </div>
        )}
      </div>

      {/* Button background bar (hidden when not hovered) */}
      <div className="hidden group-hover:block absolute left-0 right-0 bottom-0 h-20 bg-gradient-to-t from-background via-background/95 to-transparent transition-opacity duration-150 pointer-events-none" />

      {/* Action buttons (hidden when not hovered on desktop, hidden on mobile - FAB button is used instead) */}
      <div className={`${isMobile ? 'hidden' : 'hidden group-hover:flex'} absolute left-0 right-0 bottom-0 items-center justify-between px-2 py-2 transition-opacity duration-150 pointer-events-auto relative z-10 bg-background/95 backdrop-blur`}>
        <button
          onClick={() => onCreateDeal?.(stage.id)}
          className="flex-1 flex items-center justify-between px-3 py-2 text-xs rounded-lg bg-primary text-primary-foreground font-medium shadow-sm hover:shadow-md hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ease-out"
        >
          <span>{t("crm.addDeal")}</span>
          <span className="ml-2">↗</span>
        </button>
        {!isMobile && (
          <button
            aria-label={t("crm.collapse")}
            className="h-7 w-7 ml-2 rounded-lg border border-border bg-card shadow-sm text-sm hover:bg-muted hover:shadow-md hover:scale-110 active:scale-95 transition-all duration-200 ease-out"
            onClick={() => onToggleCollapse?.(stage.id)}
          >
            ⇥
          </button>
        )}
      </div>
    </div>
  );
});

KanbanColumn.displayName = 'KanbanColumn';

export default KanbanColumn;
